/* eslint-disable react-hooks/rules-of-hooks */
import React, { createContext, useContext, useEffect } from "react";
import { useAuth } from "./AuthContext";

const GlobalConfigContext = createContext();

export const useGlobalConfig = () => {
  const context = useContext(GlobalConfigContext);
  if (!context) {
    throw new Error(
      "useGlobalConfig must be used within a GlobalConfigProvider",
    );
  }
  return context;
};

export const GlobalConfigProvider = ({ children, useGlobalConfigs }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Use provided hook or create default
  const configsQuery = useGlobalConfigs
    ? useGlobalConfigs()
    : {
        data: null,
        isLoading: false,
        isError: false,
        error: null,
        refetch: () => {},
      };

  const {
    data: globalConfigs,
    features,
    isLoading,
    isError,
    error,
    refetch,
  } = configsQuery;

  // Handle API errors
  useEffect(() => {
    if (isError && error) {
      console.error("Error fetching global configs:", error);
    }
  }, [isError, error]);

  const handleRetry = () => {
    refetch();
  };

  // Determine the current state
  const getState = () => {
    // If not authenticated or still loading auth, don't show config states
    if (!isAuthenticated || authLoading) {
      return "loading";
    }
    if (isLoading) {
      return "loading";
    }
    if (isError) {
      return "error";
    }
    if (!globalConfigs || Object.keys(globalConfigs).length === 0) {
      return "empty";
    }
    return "success";
  };

  const state = getState();

  // Helper function to get a specific config value
  const getConfigValue = (key, defaultValue = null) => {
    if (!globalConfigs || !globalConfigs[key]) {
      return defaultValue;
    }
    return globalConfigs[key].value || defaultValue;
  };

  // Helper function to get a specific config object
  const getConfig = (key) => {
    if (!globalConfigs || !globalConfigs[key]) {
      return null;
    }
    return globalConfigs[key];
  };

  // Helper function to check if a config exists
  const hasConfig = (key) => {
    return !!(globalConfigs && globalConfigs[key]);
  };

  // Helper function to get all configs
  const getAllConfigs = () => {
    return globalConfigs || {};
  };

  // Helper function to get configs by category
  const getConfigsByCategory = (categoryId) => {
    if (!globalConfigs) return {};

    return Object.fromEntries(
      Object.entries(globalConfigs).filter(
        ([, config]) => config.config_category_id === categoryId,
      ),
    );
  };

  // Helper function to check if a feature is enabled
  const isFeatureEnabled = (key) => {
    return !!features?.[key]?.value;
  };

  // Helper function to get a feature value
  const getFeatureValue = (key, defaultValue = null) => {
    if (!features || !features[key]) {
      return defaultValue;
    }
    return features[key].value ?? defaultValue;
  };

  // Helper function to check if a feature exists
  const hasFeature = (key) => {
    return !!(features && features[key]);
  };

  return (
    <GlobalConfigContext.Provider
      value={{
        globalConfigs,
        features: features || {},
        getConfigValue,
        getConfig,
        hasConfig,
        getAllConfigs,
        getConfigsByCategory,
        isFeatureEnabled,
        getFeatureValue,
        hasFeature,
        state,
        isLoading,
        isError,
        error,
        handleRetry,
        isReady: state === "success",
      }}
    >
      {children}
    </GlobalConfigContext.Provider>
  );
};
